import {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from 'react';
import type { QuoteField } from '../../data/protocol/types';
import { marketDataRuntime } from '../../data/client/marketDataRuntime';
import {
  quoteStore,
  useQuoteUniverse
} from '../../data/stores/quoteStore';
import type {
  QuotesWindowConfig,
  WindowConfig,
  WindowInstance
} from '../../workspace/types';
import { Select } from '../../ui/Select/Select';
import { formatCompact } from '../../shared/format';
import {
  getQuoteGroupSymbols,
  isQuoteGroupId,
  QUOTE_MONITOR_GROUPS,
  type QuoteGroupId
} from '../../market/marketGroups';
import {
  QuoteTable,
  type QuoteSortState,
  type SortableQuoteField
} from './QuoteTable';

interface QuotesWindowProps {
  window: WindowInstance<QuotesWindowConfig>;
  onConfigChange: (
    updater: (config: WindowConfig) => WindowConfig
  ) => void;
  onOpenGraph: (symbol: string) => void;
}

const noopSubscribe = () => () => undefined;
const zeroSnapshot = () => 0;

function useQuoteSortVersion(enabled: boolean): number {
  return useSyncExternalStore(
    enabled
      ? quoteStore.subscribeStats
      : noopSubscribe,
    enabled
      ? () => quoteStore.getStats().lastSeq
      : zeroSnapshot,
    zeroSnapshot
  );
}

export function QuotesWindow({
  window,
  onConfigChange,
  onOpenGraph
}: QuotesWindowProps) {
  const universe = useQuoteUniverse();

  const [filter, setFilter] = useState('');
  const [sort, setSort] =
    useState<QuoteSortState>(null);

  const activeGroup = getActiveGroup(
    window.config.groupId
  );

  const sortVersion = useQuoteSortVersion(
    Boolean(sort)
  );

  const allSymbols = universe.length
    ? universe
    : window.config.symbols;

  const groupSymbols = useMemo(() => {
    return getQuoteGroupSymbols(
      activeGroup,
      allSymbols
    );
  }, [
    activeGroup,
    allSymbols
  ]);

  const subscribedSymbols = useMemo(() => {
    return groupSymbols.slice(
      0,
      window.config.maxRows
    );
  }, [
    groupSymbols,
    window.config.maxRows
  ]);

  const visibleSymbols = useMemo(() => {
    const query = filter
      .trim()
      .toUpperCase();

    const filtered = query
      ? subscribedSymbols.filter((symbol) =>
          symbol.includes(query)
        )
      : subscribedSymbols;

    if (!sort) {
      return filtered;
    }

    return [...filtered].sort(
      (left, right) =>
        compareSymbols(left, right, sort)
    );
  }, [
    filter,
    subscribedSymbols,
    sort,
    sortVersion
  ]);

  const tableViewKey = useMemo(() => {
    return [
      activeGroup,
      filter.trim().toUpperCase(),
      sort?.field ?? 'unsorted',
      sort?.direction ?? 'none',
      window.config.maxRows,
      window.config.rowHeight,
      subscribedSymbols.join(',')
    ].join('|');
  }, [
    activeGroup,
    filter,
    sort,
    subscribedSymbols,
    window.config.maxRows,
    window.config.rowHeight
  ]);

  useEffect(() => {
    if (subscribedSymbols.length === 0) {
      return undefined;
    }

    return marketDataRuntime.subscribeQuotes(
      window.id,
      subscribedSymbols,
      window.config.fields as QuoteField[]
    );
  }, [
    window.id,
    subscribedSymbols,
    window.config.fields
  ]);

  const handleSort = (
    field: SortableQuoteField
  ) => {
    setSort((current) => {
      if (
        !current ||
        current.field !== field
      ) {
        return {
          field,
          direction: 'desc'
        };
      }

      if (current.direction === 'desc') {
        return {
          field,
          direction: 'asc'
        };
      }

      return null;
    });
  };

  const changeGroup = (
    groupId: QuoteGroupId
  ) => {
    setFilter('');

    onConfigChange((config) => ({
      ...config,
      groupId
    } as QuotesWindowConfig));
  };

  return (
    <div className="quotes-window window-content">
      <div className="window-toolbar quotes-window__toolbar no-window-drag">
        <div
          className="quote-tabs"
          role="tablist"
          aria-label="Quote groups"
        >
          {QUOTE_MONITOR_GROUPS.map((group) => (
            <button
              key={group.id}
              type="button"
              role="tab"
              aria-selected={
                activeGroup === group.id
              }
              className={
                activeGroup === group.id
                  ? 'quote-tabs__button quote-tabs__button--active'
                  : 'quote-tabs__button'
              }
              onClick={() =>
                changeGroup(group.id)
              }
            >
              {group.label}
            </button>
          ))}
        </div>

        <span className="toolbar-stat quotes-window__count">
          {formatCompact(visibleSymbols.length)} symbols
        </span>

        <input
          className="terminal-input quotes-window__search"
          value={filter}
          onChange={(event) =>
            setFilter(event.target.value)
          }
          placeholder="Search symbols"
          aria-label="Search quote symbols"
        />

        <div className="quotes-window__controls">
          <Select
            label="Rows"
            value={window.config.maxRows}
            onChange={(event) =>
              onConfigChange((config) => ({
                ...config,
                maxRows: Number(
                  event.target.value
                )
              } as QuotesWindowConfig))
            }
          >
            {[50, 100, 200, 400, 600, 800].map(
              (count) => (
                <option
                  key={count}
                  value={count}
                >
                  {count}
                </option>
              )
            )}
          </Select>

          <Select
            label="Density"
            value={window.config.rowHeight}
            onChange={(event) =>
              onConfigChange((config) => ({
                ...config,
                rowHeight: Number(
                  event.target.value
                )
              } as QuotesWindowConfig))
            }
          >
            <option value={26}>
              Compact
            </option>
            <option value={30}>
              Normal
            </option>
            <option value={36}>
              Comfort
            </option>
          </Select>
        </div>
      </div>

      <QuoteTable
        symbols={visibleSymbols}
        fields={window.config.fields}
        rowHeight={window.config.rowHeight}
        sort={sort}
        viewKey={tableViewKey}
        emptyMessage={`No ${getGroupLabel(activeGroup)} symbols are available.`}
        onSort={handleSort}
        onSymbolDoubleClick={onOpenGraph}
      />
    </div>
  );
}

function getActiveGroup(
  value: string | undefined
): QuoteGroupId {
  return isQuoteGroupId(value)
    ? value
    : 'tech';
}

function getGroupLabel(
  groupId: QuoteGroupId
): string {
  return QUOTE_MONITOR_GROUPS.find(
    (group) => group.id === groupId
  )?.label ?? groupId;
}

function compareSymbols(
  leftSymbol: string,
  rightSymbol: string,
  sort: NonNullable<QuoteSortState>
): number {
  const left =
    quoteStore.getQuote(leftSymbol)[sort.field] ??
    0;

  const right =
    quoteStore.getQuote(rightSymbol)[sort.field] ??
    0;

  const result =
    Number(left) - Number(right);

  if (result === 0) {
    return leftSymbol.localeCompare(
      rightSymbol
    );
  }

  return sort.direction === 'asc'
    ? result
    : -result;
}